/**
 * File: useMobileNavController.ts
 * Path: apps/admin/src/modules/layout/hooks/useMobileNavController.ts
 * Description: The mobile bar: which menu items sit in the bottom dock and which in the More drawer, whether
 * the drawer is open, and the sign-out action.
 *
 * The dock holds Today, then one button per group that has its own page (CRM, Accounting, CMS), then
 * More. More holds the groups without a page of their own (Tools, System), so it stays short. Both come from
 * the shared menu list, so a page added to a group shows up by itself.
 */

import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ADMIN_PATHS } from "@idk/config";
import { useAuthMutations } from "@/modules/auth/hooks/useAuthMutations";
import { useMenuItems } from "@/modules/layout/hooks/useMenuItemsQueries";

export const useMobileNavController = () => {
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const { logout, isPending } = useAuthMutations();
  const { pathname } = useLocation();

  const filteredItems = useMenuItems();

  const { dockItems, drawerItems } = useMemo(() => {
    const dock = filteredItems.filter((item) => item.hub || item.href === ADMIN_PATHS.dashboard);
    const drawer = filteredItems.filter((item) => !dock.includes(item));
    return { dockItems: dock, drawerItems: drawer };
  }, [filteredItems]);

  return {
    pathname,
    isMoreOpen,
    dockItems,
    drawerItems,
    isPending,
    closeMenu: () => setIsMoreOpen(false),
    toggleMore: () => setIsMoreOpen(!isMoreOpen),
    signOut: () => {
      setIsMoreOpen(false);
      logout();
    },
  };
};
